import { useNavigate } from "react-router-dom";
import { Grid, IconButton, Tooltip, Typography } from "@mui/material";
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';

import { columns } from "./grid-columns";
import { UserPermissionName } from "../../models/user-permission-name";
import { SiTrackGrid } from "../../components/si-track-grid/si-track-grid";
import { useFormContext } from "react-hook-form";
import { useLoadData } from "../../hooks/useLoadData";
import { ApiResponse } from "../../models/api-response";
import { GridColumnConfig, GridPreference } from "../../models/gridCoumnConfig";
import { gridApiKeyStore } from "../../api/grid-api-key-store";
import { useSaveData } from "../../hooks/useSaveData";
import { useEffect, useState } from "react";
import { State } from "@progress/kendo-data-query";
import useDebounce from "../../hooks/use-debounce";
import { Inspector } from "../../models/inspector";
import { GuardedComponent } from "../../components/guarded-component";

const styling = {
    gridTextStyle: { paddingLeft: 5, cursor: 'pointer', fontWeight: 'bold' }
};

interface CustomGridCellProps {
    field: string;
}

interface InspectorComponent {
    inspectorData: any;
    colorCode: any;
    scheduleId: any;
    selectSingleInspector: (id: any) => void;
    isLoading: boolean;
    filtersData: any;
}

const getInspectorWithFilters = (filter: any) => {
    const { customerID, offices, projects, marketAreas, inspectors, workOrderStatusID, scheduledDateFrom, scheduledDateTo, divisions } = filter;

    let queryString = '';

    if (customerID) {
        queryString += `${queryString ? '&' : ''}customerID=${customerID}`;
    }
    if (offices) {
        queryString += `${queryString ? '&' : ''}offices=${offices}`;
    }
    if (projects) {
        queryString += `${queryString ? '&' : ''}projects=${projects}`;
    }
    if (marketAreas) {
        queryString += `${queryString ? '&' : ''}marketAreas=${marketAreas}`;
    }
    if (inspectors) {
        queryString += `${queryString ? '&' : ''}inspectors=${inspectors}`;
    }
    if (workOrderStatusID) {
        queryString += `${queryString ? '&' : ''}workOrderStatusID=${workOrderStatusID}`;
    }
    if (scheduledDateFrom) {
        queryString += `${queryString ? '&' : ''}dateFrom=${scheduledDateFrom}`;
    }
    if (scheduledDateTo) {
        queryString += `${queryString ? '&' : ''}dateTo=${scheduledDateTo}`;
    }
    if (divisions) {
        queryString += `${queryString ? '&' : ''}divisions=${divisions}`;
    }
    return queryString;
};


export const InspectorComponent = ({ inspectorData, colorCode, scheduleId, selectSingleInspector, isLoading, filtersData }: InspectorComponent) => {

    const navigate = useNavigate();
    const { getValues } = useFormContext();
    const handleViewLeave = (inspector: any) => {
        selectSingleInspector(Number(inspector?.inspectorID));
    }

    const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
        if (field === 'inspectorName') {
            return (dataItem: any) => (
                <td>
                    <Grid
                        container
                        direction="row"
                        sx={{
                            justifyContent: "flex-start",
                            alignItems: "flex-start",
                        }}
                    >
                        <div style={{
                            padding: 10,
                            borderRadius: 50,
                            backgroundColor: colorCode[Number(dataItem?.dataItem?.inspectorID)]
                        }}></div>
                        <div onClick={() => handleViewLeave(dataItem?.dataItem)}>
                            <Typography style={styling.gridTextStyle} variant="body2">{dataItem?.dataItem?.inspectorName ?? "Name Missing"}</Typography>
                        </div>
                    </Grid>
                </td>
            );
        }
    };

    const schedulingRedirect = (inspectorID: any) => {
        if (filtersData.inspectors === undefined) {
            filtersData.inspectors = [inspectorID];
        }
        filtersData.workOrderStatusID = scheduleId;
        navigate(`/scheduling?${getInspectorWithFilters(filtersData)}`);
    }

    const redirectBulkScheduleElement = (dataItem: any) => {
        return (
            // <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
            <IconButton aria-label="move" onClick={() => schedulingRedirect(dataItem?.dataItem?.inspectorID)}>
                <Tooltip title={<Typography variant="body1">Schedule</Typography>}>
                    <ArrowCircleRightOutlinedIcon />
                </Tooltip>
            </IconButton>
            // </GuardedComponent>
        );
    }

    // -------------------------------------------------Grid Layout------------------------------

    const {
        isLoading: isLoadingGridLayout,
        data: gridLayouts,
        refetch: refetchGridLayout
    } = useLoadData<ApiResponse<GridPreference>>(
        gridApiKeyStore.getGridLayout('InspectorCalendar', 'InspectorCalendar')
    );

    const { mutateAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
    const [gridConfig, setGridConfig] = useState<{
        dataState: State | undefined;
        columns: any[] | undefined;
    }>();

    const [filteredInspectors, setFilteredInspectors] = useState<Inspector[]>([]);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    useEffect(() => {
        const filteredInspectorList = inspectorData?.filter((Inspector: any) =>
            Object.values(Inspector).some(
                (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
            )
        );
        setFilteredInspectors(filteredInspectorList || []);
    }, [debouncedSearchTerm, inspectorData]);

    const handleInputChange = (value: string) => {
        setSearchTerm(value);
    };

    const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
        const data = {
            filterJSON: JSON.stringify(gridDataState),
            columnJSON: JSON.stringify(gridColumns),
            gridName: 'InspectorCalendar',
            pageName: 'InspectorCalendar',
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    const handleResetLayout = () => {
        const data = {
            filterJSON: null,
            columnJSON: null,
            gridName: 'InspectorCalendar',
            pageName: 'InspectorCalendar',
        };

        mutateAsync({ data }).then(() => {
            refetchGridLayout();
        });
    };

    useEffect(() => {
        if (!gridLayouts?.data) return;

        try {
            const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

            const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

            // if (filterData && columnJson) {
            setGridConfig({
                columns: columnJson,
                dataState: filterData,
            });
            // }
        } catch (error) {
            console.log('Error parsing grid layout JSON:', error);
        }
    }, [gridLayouts?.data]);

    // ----------------------------------------------------------------------------------------

    return (
        <SiTrackGrid
            fileTitle='inspectior_calendar'
            columns={columns}
            dataItemKey="inspectorID"
            customCell={(field) =>
                CustomGridCell({
                    field,
                })
            }
            CustomComponent={redirectBulkScheduleElement}
            actionPermissions={{
                export: UserPermissionName.DOWNLOAD_GRID
            }}
            isBusy={isLoading || isLoadingGridLayout}

            // -----------------Grid Layout-------
            dataItems={filteredInspectors || []}
            reorderable
            handleSearch={handleInputChange}
            handleSaveLayout={handleSaveLayout}
            isSaveLayout={true}
            gridConfigFromApi={gridConfig}
            handleResetLayout={handleResetLayout}
        // -----------------Grid Layout-------
        />
    )
}